import React from "react";
import type { GetStaticProps, NextPage } from "next";
import { Text, Box } from "@chakra-ui/react";
import {
  type IntegrationSimple,
  type Banner,
  type Page as SanityPage,
  type Customer,
  getAnnouncementBanner,
  getPage,
  getSimpleIntegrations,
  getStackedLogos,
} from "lib/sanity";
import { Page } from "components/page";
import { Section } from "components/section";
import { Customer360Hero } from "components/customer-360-hero";
import { StackedLogos } from "components/logos";
import { PairVideo } from "components/pair-video";
import { Quote } from "components/quotes";
import { CdpLeader } from "components/cdp-leader";
import DestinationMarquee from "components/destination-marquee";
import { getPageMetaImage } from "utils";

type PageProps = {
  page: SanityPage;
  destinations: IntegrationSimple[];
  announcementBanner?: Banner;
  customerRows: Customer[][];
};

const Customer360: NextPage<Readonly<PageProps>> = ({ page, destinations, announcementBanner, customerRows }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <Customer360Hero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      <StackedLogos customerRows={customerRows} />

      <Section my={0}>
        <Box textAlign="center">
          <Text as="h2" textStyle="h3">
            Prepare your data for activation
          </Text>

          <Text fontSize="lg" mt={5} maxW="3xl" mx="auto">
            Your data warehouse should be the center of your business. Hightouch now helps you enhance and curate the
            data stored within it to unlock its full potential.
          </Text>
        </Box>

        <Section mt={{ base: 24, md: 32 }}>
          <PairVideo
            align="left"
            eyebrow="Identity resolution"
            title="Create 360° user profiles"
            content={
              <Text>
                Stitch your existing customer data into into accurate and actionable profiles directly in your data
                warehouse without writing a single line of code.
              </Text>
            }
            actionTitle="Learn more about identity resolution"
            actionHref="/platform/identity-resolution"
            animationWebm="https://cdn.sanity.io/files/pwmfmi47/production/df27b13fe19e0e7b6e727d842afd4e9b1a8c7062.webm"
            animationMp4="https://cdn.sanity.io/files/pwmfmi47/production/c380cff9ceb11836df0ae7d92162eac69cb711c0.mp4"
            animationPoster="https://cdn.sanity.io/images/pwmfmi47/production/f3f554217014a0ced082c093f913b3a7b653ba04-1904x1446.webp"
            animationBorderColor="base.border"
          />
        </Section>

        <Section my={{ base: 32, md: 40 }}>
          <PairVideo
            align="right"
            eyebrow="Entity resolution"
            title="Unify any record"
            content={
              <Text>
                Need to measure performance at the household level? Track pets owned by each customer? Or monitor
                multiple B2B accounts? Resolve any entity your business cares about directly in your data warehouse.
              </Text>
            }
            actionTitle="Learn more about entity resolution"
            actionHref="/platform/identity-resolution"
            animationWebm="https://cdn.sanity.io/files/pwmfmi47/production/074ddac8fb02bd1e610d64ceb9960f3cd935d3f6.webm"
            animationMp4="https://cdn.sanity.io/files/pwmfmi47/production/37beb3e86b74049213936ba00f5fbc97bd7fd005.mp4"
            animationPoster="https://cdn.sanity.io/images/pwmfmi47/production/d062c9477fb7323c4ab5fcaf6e2b5d2e306c570f-1982x1512.webp"
            animationBorderColor="base.border"
          />
        </Section>

        <Section my={{ base: 32, md: 40 }}>
          <PairVideo
            align="left"
            eyebrow="Schema builder"
            title="Curate your customer data models"
            content={
              <Text>
                Define the key data models and events that marketers can build audiences from in a visual UI. Build as
                many or as few models as you need quickly and easily.
              </Text>
            }
            actionTitle="Learn more about schemas"
            actionHref="/blog/announcing-schema-builder"
            animationWebm="https://cdn.sanity.io/files/pwmfmi47/production/d33b58631c9c94af101963c34e4f5243066040cf.webm"
            animationMp4="https://cdn.sanity.io/files/pwmfmi47/production/a4caea7deeb78dc2913b8115ef2ef3e837e98851.mp4"
            animationPoster="https://cdn.sanity.io/images/pwmfmi47/production/0593abc1d008d4e17d90edbcc0a4227b9516c1c6-1926x1468.webp"
            animationBorderColor="base.border"
          />
        </Section>

        <Section my={{ base: 32, md: 40 }}>
          <PairVideo
            align="right"
            eyebrow="Coming soon: Warehouse enrichment"
            title="Augment your data"
            content={
              <Text>
                Enhance your first-party customer profiles with vetted third-party data. Power every decision with even
                richer customer profiles.
              </Text>
            }
            animationWebm="https://cdn.sanity.io/files/pwmfmi47/production/1703a17cf7df388ca0f025f99ff02c235a46ed82.webm"
            animationMp4="https://cdn.sanity.io/files/pwmfmi47/production/25c90ae392fc2380f9f14fc71f6560cd71a00e2e.mp4"
            animationPoster="https://cdn.sanity.io/images/pwmfmi47/production/36fa239e24d5a61187fdbb914b3d217f89d8e803-1902x1446.webp"
            animationBorderColor="base.border"
          />
        </Section>
      </Section>

      <Section>
        <Quote
          quote="The Customer 360 Toolkit is a game changer that helps brands quickly establish the data foundation for best-in-class customer experiences - all without the need for extensive engineering resources or compromising data security."
          author="Dio Favatas"
          authorImage="https://cdn.sanity.io/images/pwmfmi47/production/080a09612bf9e497d21831af2efe33eaea2fea35-816x720.png"
          job="Head of Identity and Marketing Clouds"
          company="Tredence"
          logo="https://cdn.sanity.io/images/pwmfmi47/production/97560aa155c39dee4073d85f5ec7dad323c0cb68-600x66.png"
        />
      </Section>

      <CdpLeader />

      <DestinationMarquee destinations={destinations} />
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, destinations, announcementBanner, customerRows] = await Promise.all([
    getPage("/platform/customer-360"),
    getSimpleIntegrations("destination"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      destinations,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};

export default Customer360;
